import { ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal } from '@angular/core';
import { IonMenu } from '@ionic/angular';
import { CurafidaAuthService } from '../../../authentication/curafida-auth.service';
import { CurafidaSegmentItem } from '../../../common/entities/curafida-segment.item';
import { SegmentType } from '../../../common/entities/segment.type';
import { ConfigService } from '../../../config/services/config.service';
import { UserDetailTabs } from '../../pages/user-management/user-detail.tabs';
import { isTenantRootGroupMember } from '../../services/patient-menu-actions';
import { PatientSidePanelContentComponent } from '../patient-side-panel-content/patient-side-panel-content.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { UserPreferencesService } from '../../services/user-preferences/user-preferences.service';
import { PatientSidePanelService } from '../../services/patient-side-panel/patient-side-panel.service';
import { PATIENT_SIDE_PANEL_SETTING_NAME, PatientSidePanelSetting } from './patient-side-panel.setting';

/**
 * The side panel of the app shell, the second menu next to the main menu: an ion-menu at the end side with the master
 * data, notes and tasks of the patient on screen. It is only enabled on the pages about one patient, which show their
 * patient through the PatientSidePanelService and get the changes made in the panel back through it.
 */
@Component({
    selector: 'lib-patient-side-panel',
    templateUrl: './patient-side-panel.component.html',
    styleUrls: ['./patient-side-panel.component.scss'],
    // The master data in the panel is still mutation-based; an OnPush host would skip refreshing it after its loads.
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [IonMenu, PatientSidePanelContentComponent],
})
export class PatientSidePanelComponent {
    private readonly configService = inject(ConfigService);
    private readonly userPreferencesService = inject(UserPreferencesService);
    private readonly authService = inject(CurafidaAuthService);
    protected readonly sidePanel = inject(PatientSidePanelService);

    /** The id of the ion-content or router outlet the menu lies over */
    readonly contentId = input.required<string>();

    /** The patient of the page on screen; the panel is disabled on any other page */
    readonly patient = this.sidePanel.patient;
    /** Who the patient is; saving the master data hands over the reloaded patient, which is still the same one */
    private readonly patientUuid = computed(() => this.patient()?.uuid);
    /**
     * The panel is only rendered while the menu is open: its tabs load nothing in the background and open fresh every
     * time. Another patient on screen, or none, drops it.
     */
    readonly isOpen = linkedSignal<string | undefined, boolean>({ source: this.patientUuid, computation: () => false });
    private readonly savedSetting = toSignal(
        this.userPreferencesService.settingByName<PatientSidePanelSetting>(PATIENT_SIDE_PANEL_SETTING_NAME),
    );
    /**
     * The tab the panel was left on, kept in the user preferences like the tab of its task list, so it opens on it
     * again, also after a reload. Set at once on a switch, as saving waits for the backend.
     */
    readonly activeTab = linkedSignal(() => this.savedSetting()?.activeTab ?? SegmentType.MAIN_INFORMATION);
    /** The tabs of the patient record that the panel offers as well; only those the logged-in user may see */
    readonly tabs = computed<CurafidaSegmentItem<SegmentType>[]>(() => {
        const patient = this.patient();
        if (!patient) return [];
        const loggedInUser = this.authService.getSession().user;
        const tabs = new UserDetailTabs(
            this.configService.frontendConfig().features,
            this.configService.config()?.configuration.features,
            loggedInUser,
        );
        tabs.init({ user: patient, isMemberOfTenant: isTenantRootGroupMember(loggedInUser) });
        return tabs.sidePanelItems();
    });

    selectTab(tab: SegmentType): void {
        this.activeTab.set(tab);
        this.userPreferencesService.updateFilterSettingItem(new PatientSidePanelSetting(tab));
    }

    async close(): Promise<void> {
        await this.sidePanel.close();
    }
}
